import React, { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, Typography as MuiTypography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Button,
  Menu,
  MenuItem,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@material-ui/core";
import NavigationTabs from "./NavigationTabs";
import DataTable from "./DataTable";
import CreateShipmentModal from "./CreateShipmentModal";
import PropTypes from "prop-types";
import { ReactComponent as CreateShipmentIcon } from "../images/create-shipment-icon.svg";
import { ReactComponent as PrintLabelIcon } from "../images/print-label-icon.svg";
import { ReactComponent as ManifestIcon } from "../images/manifest-icon.svg";
import { useStoreContext } from "../contexts/storeContext";
import { useUserContext } from "../contexts/userContext";
import {
  getApiBaseUrl,
  getApiBaseUrlFallback,
  isCorsOrNetworkError,
} from "../config/env";
import ArrowDownIcon from "./ArrowDownIcon";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    marginTop: "32px",
    border: "1px solid #D5E0EB",
    borderRadius: "8px",
    padding: theme.spacing(3),
    backgroundColor: "#FFFFFF",
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  title: {
    color: "#212529",
    fontSize: 24,
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    letterSpacing: "-0.24px",
  },
  searchBarContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    width: "100%",
  },
  combinedSearchBar: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #DEE2E6",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    height: "40px",
    flex: 1,
  },
  searchSection: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1.5),
    minWidth: 0,
  },
  searchIcon: {
    width: 16,
    height: 16,
    color: "#6C757D",
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    color: "#212529",
    backgroundColor: "transparent",
    "&::placeholder": {
      color: "#6C757D",
    },
  },
  separator: {
    width: "1px",
    height: "24px",
    backgroundColor: "#DEE2E6",
    flexShrink: 0,
  },
  filterSection: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1.5),
    cursor: "pointer",
    minWidth: 150,
    "&:hover": {
      backgroundColor: "#F8F9FA",
    },
  },
  filterText: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    color: "#495057",
    marginRight: theme.spacing(0.5),
    flex: 1,
  },
  chevronIcon: {
    width: 12,
    height: 12,
    color: "#6C757D",
    flexShrink: 0,
  },
  filterMenuPaper: {
    padding: 0,
    border: "1px solid #D5E0EB",
    borderRadius: 6,
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
  },
  filterMenuItem: {
    minHeight: 32,
    height: 32,
    padding: theme.spacing(0, 1.5),
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "100%",
    letterSpacing: "0%",
    color: "#495057",
    "&:hover": {
      backgroundColor: "#E4DAFF80",
    },
    "&.Mui-selected": {
      backgroundColor: "#E4DAFF80",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#E4DAFF80",
    },
    "&:not(:last-child)": {
      borderBottom: "1px solid #D5E0EB",
    },
  },
  createButton: {
    backgroundColor: "#7650E1",
    color: "#FFFFFF",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#5A32A3",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "#E1E1E1",
      color: "#8F8F8F",
    },
    "&:disabled $buttonIcon": {
      color: "#8F8F8F",
    },
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  loadingState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
  },
  buttonIcon: {
    width: 16,
    height: 16,
    marginRight: theme.spacing(1),
    color: "#FFFFFF",
  },
  nestedDevicesContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#FFFFFF",
  },
  nestedDevicesTitle: {
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    color: "#212529",
    marginBottom: theme.spacing(1.5),
  },
  nestedTableWrapper: {
    maxHeight: "400px",
    overflowY: "auto",
    border: "1px solid #DEE2E6",
    borderRadius: "8px",
  },
  nestedTable: {
    width: "100%",
  },
  nestedHeaderCell: {
    backgroundColor: "#E4DAFF",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "#212529",
    lineHeight: "20px",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #DEE2E6",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  nestedBodyCell: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
    fontWeight: 400,
    color: "#212529",
    lineHeight: "100%",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #D5E0EB",
  },
  nestedRow: {
    backgroundColor: "#FFFFFF",
    "&:nth-of-type(even)": {
      backgroundColor: "#F8F9FA",
    },
  },
}));

const SHIPMENTS_ACTIVE_SUBTAB_STORAGE_KEY = "shipments-active-subtab";

/**
 * PendingDevices Component
 * 
 * Displays pending devices with tabs, search, filter, and data table
 * 
 * @param {Array} data - Array of device objects
 * @param {Function} onCreateShipment - Handler for create shipment button
 * @param {Function} onTabChange - Handler for tab changes
 * @param {string} className - Additional CSS classes
 */
const PendingDevices = ({
  data,
  shippedData,
  pendingLoading,
  shippedLoading,
  onCreateShipment,
  onTabChange,
  className,
  boxInventory = [],
  // Pagination props
  pendingPage = 1,
  pendingPageSize = 10,
  pendingTotal = 0,
  onPendingPageChange,
  onPendingPageSizeChange,
  shippedPage = 1,
  shippedPageSize = 10,
  shippedTotal = 0,
  onShippedPageSizeChange,
  onShippedPageChange,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { user } = useUserContext();
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") {
      return "pending";
    }
    const savedTab = localStorage.getItem(
      SHIPMENTS_ACTIVE_SUBTAB_STORAGE_KEY
    );
    return savedTab === "shipped" ? "shipped" : "pending";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterKey, setFilterKey] = useState("tradeInId");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  const tabs = [
    { id: "pending", label: t("Pending Devices") },
    { id: "shipped", label: t("Shipped Devices") },
  ];

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.setItem(SHIPMENTS_ACTIVE_SUBTAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterSelect = (value) => {
    setFilterKey(value);
    handleFilterClose();
  };

  const handleRowSelect = (selectedIndices, selectedData) => {
    // For server-side pagination, we need to track selections by device ID
    // since we don't have all data at once
    setSelectedDevices((prevSelected) => {
      // Create a Map of existing selections by their unique identifier
      const existingMap = new Map();
      prevSelected.forEach((device) => {
        const key = device.id || device.tradeInId || `${device.modelId}-${device.tradeInId}`;
        if (key) {
          existingMap.set(key, device);
        }
      });
      
      // Get current page data (filteredPendingData contains only current page data in server-side pagination)
      const currentPageData = filteredPendingData;
      
      // Create a Set of device keys that are on the current page
      const currentPageKeys = new Set();
      currentPageData.forEach((device) => {
        const key = device.id || device.tradeInId || `${device.modelId}-${device.tradeInId}`;
        if (key) {
          currentPageKeys.add(key);
        }
      });
      
      // Create a Set of device keys that are currently selected on this page
      // Only consider devices that are actually on the current page
      const currentPageSelectedKeys = new Set();
    if (selectedData && selectedData.length > 0) {
        selectedData.forEach((device) => {
          const key = device.id || device.tradeInId || `${device.modelId}-${device.tradeInId}`;
          // Only add if this device is on the current page
          if (key && currentPageKeys.has(key)) {
            currentPageSelectedKeys.add(key);
          }
        });
      }
      
      // Process each item in the current page ONLY
      // This ensures we only update selections for items visible on the current page
      currentPageData.forEach((device) => {
        const key = device.id || device.tradeInId || `${device.modelId}-${device.tradeInId}`;
        if (!key) return;
        
        const isCurrentlySelected = currentPageSelectedKeys.has(key);
        const wasPreviouslySelected = existingMap.has(key);
        
        if (isCurrentlySelected && !wasPreviouslySelected) {
          // New selection - add it
          existingMap.set(key, device);
        } else if (!isCurrentlySelected && wasPreviouslySelected) {
          // Deselection - remove it (device is on current page but not selected)
          existingMap.delete(key);
        }
        // If both are selected or both are not selected, no change needed
      });
      
      return Array.from(existingMap.values());
    });
  };

  // Function to check if a device (row) is selected
  const isRowSelected = (row) => {
    if (!row || selectedDevices.length === 0) return false;
    const rowKey = row.id || row.tradeInId || `${row.modelId}-${row.tradeInId}`;
    if (!rowKey) return false;
    
    return selectedDevices.some((device) => {
      const deviceKey = device.id || device.tradeInId || `${device.modelId}-${device.tradeInId}`;
      return deviceKey === rowKey;
    });
  };

  const handleCreateShipmentClick = () => {
    if (selectedDevices.length > 0) {
      setIsModalOpen(true);
    } else {
      // Show message or alert that no devices are selected
      console.log("Please select at least one device");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Don't clear selections here - only clear after successful shipment creation
  };

  const handleCreateShipment = (shipmentData) => {
    if (onCreateShipment) {
      onCreateShipment(shipmentData);
    }
    // Clear selection after creating shipment
    setSelectedDevices([]);
    setSelectionResetKey((key) => key + 1);
  };

  const handleManifestClick = async (row) => {
    if (!row?.id) {
      return;
    }
    const storeId = user?.store?.id;
    if (!storeId) {
      console.error("Missing store ID for manifest request.");
      return;
    }

    const baseUrl = getApiBaseUrl();
    const fallbackUrl = getApiBaseUrlFallback();
    const primary = baseUrl || fallbackUrl;
    if (!primary) {
      console.error("Missing API base URL for manifest request.");
      return;
    }

    const tryManifest = async (base) => {
      const url = `${base.replace(/\/$/, "")}/shipment/manifest-file/${row.id}`;
      const token = localStorage.getItem("userId");
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/pdf",
          "X-Store-Id": storeId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        throw new Error(`Manifest request failed with ${response.status}`);
      }
      return response.blob();
    };

    try {
      const blob = await tryManifest(primary);
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      if (
        fallbackUrl &&
        baseUrl &&
        baseUrl !== fallbackUrl &&
        isCorsOrNetworkError(error)
      ) {
        try {
          const blob = await tryManifest(fallbackUrl);
          const url = window.URL.createObjectURL(blob);
          window.open(url, "_blank", "noopener,noreferrer");
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (fallbackError) {
          console.error("Failed to load manifest PDF (fallback).", fallbackError);
        }
      } else {
        console.error("Failed to load manifest PDF.", error);
      }
    }
  };

  /** Dates always English (weekday/month); not tied to app i18n locale */
  const getLocaleCode = () => {
    const language = i18n.language || "en";
    return language.split(/[-_]/)[0] || "en";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";

    const localeCode = getLocaleCode();
    const formattedDate = new Intl.DateTimeFormat(localeCode, {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);

    const now = new Date();
    const diffMinutes = Math.max(0, Math.floor((now - date) / 60000));
    if (diffMinutes < 60) {
      return `${formattedDate} - ${diffMinutes} ${t(
        diffMinutes === 1 ? "minute ago" : "minutes ago"
      )}`;
    }
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `${formattedDate} - ${diffHours} ${t(
        diffHours === 1 ? "hour ago" : "hours ago"
      )}`;
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) {
      return `${formattedDate} - ${diffDays} ${t(
        diffDays === 1 ? "day ago" : "days ago"
      )}`;
    }
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
      return `${formattedDate} - ${diffMonths} ${t(
        diffMonths === 1 ? "month ago" : "months ago"
      )}`;
    }
    const diffYears = Math.floor(diffDays / 365);
    return `${formattedDate} - ${diffYears} ${t(
      diffYears === 1 ? "year ago" : "years ago"
    )}`;
  };

  const pendingColumns = [
    {
      key: "tradeInId",
      label: t("Trade-In ID"),
      sortable: true,
    },
    {
      key: "modelId",
      label: t("Model ID"),
      sortable: true,
    },
    {
      key: "tradeInDate",
      label: t("Trade-In Date"),
      sortable: true,
      render: (value) => formatDate(value),
    },
  ];

  const shippedColumns = [
    {
      key: "shipmentNo",
      label: t("Shipment No."),
      sortable: true,
    },
    {
      key: "trackingNo",
      label: t("Tracking No."),
      sortable: true,
      render: (value, row) => {
        if (row.trackingLink) {
          return (
            <a
              href={row.trackingLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
              }}
              style={{
                color: "#6F42C1",
                textDecoration: "none",
                fontFamily: "Inter, sans-serif",
                fontSize: "13px",
                fontWeight: 400,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
              onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
            >
              {value || "—"}
            </a>
          );
        }
        return <span>{value || "—"}</span>;
      },
    },
    {
      key: "created",
      label: t("Created"),
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: "numDevices",
      label: t("No. of Devices"),
      sortable: true,
    },
    {
      key: "actions",
      label: t("Actions"),
      sortable: false,
      render: (value, row) => (
        <div style={{ display: "flex", gap: "16px" }}>
          <button
            type="button"
            onClick={() => {
              if (row.inboundShippingLabelUrl) {
                window.open(
                  row.inboundShippingLabelUrl,
                  "_blank",
                  "noopener,noreferrer"
                );
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              opacity: row.inboundShippingLabelUrl ? 1 : 0.5,
            }}
            title={t("Print Label")}
          >
            <PrintLabelIcon
              style={{
                width: "20px",
                height: "20px",
                color: "#6F42C1",
              }}
            />
            </button>
          <button
            type="button"
            onClick={() => {
              handleManifestClick(row);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              opacity: row.id ? 1 : 0.5,
            }}
            title={t("Manifest")}
          >
            <ManifestIcon
              style={{
                width: "20px",
                height: "20px",
                color: "#6F42C1",
              }}
            />
          </button>
        </div>
      ),
    },
  ];

  const pendingSearchOptions = [
    { key: "tradeInId", label: t("Trade-In ID") },
    { key: "modelId", label: t("Model ID") },
    { key: "tradeInDate", label: t("Trade-In Date") },
  ];
  const shippedSearchOptions = [
    { key: "shipmentNo", label: t("Shipment No.") },
    { key: "trackingNo", label: t("Tracking No.") },
    { key: "created", label: t("Created") },
  ];

  const getSearchValue = (item, key) => {
    const value = item?.[key];
    if (!value) return "";
    if (key === "tradeInDate" || key === "created") {
      return formatDate(value);
    }
    return String(value);
  };

  const filteredPendingData = data.filter((item) => {
    if (!searchQuery) return true;
    const value = getSearchValue(item, filterKey);
    return value.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredShippedData = (shippedData || []).filter((item) => {
    if (!searchQuery) return true;
    const value = getSearchValue(item, filterKey);
    return value.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const renderExpandedDevices = (row) => {
    const devices = row.devices || [];
    const deviceColumns = [
      { key: "tradeInId", label: t("Trade-In ID"), sortable: true },
      { key: "modelId", label: t("Model ID"), sortable: true },
      { key: "tradeInDate", label: t("Trade-In Date"), sortable: true },
    ];

    return (
      <div className={classes.nestedDevicesContainer}>
        <MuiTypography className={classes.nestedDevicesTitle}>
          {t("Devices")}
        </MuiTypography>
        <div className={classes.nestedTableWrapper}>
          <Table className={classes.nestedTable}>
            <TableHead>
              <TableRow>
                {deviceColumns.map((column) => (
                  <TableCell key={column.key} className={classes.nestedHeaderCell}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {column.label}
                      {column.sortable && (
                        <svg
                          style={{
                            width: 12,
                            height: 12,
                            marginLeft: 4,
                            color: "#495057",
                          }}
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 10L12 15L17 10H7Z"
                            fill="currentColor"
                          />
                        </svg>
                      )}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device, deviceIndex) => (
                <TableRow
                  key={deviceIndex}
                  className={classes.nestedRow}
                  style={{
                    backgroundColor: deviceIndex % 2 === 0 ? "#FFFFFF" : "#F8F9FA",
                  }}
                >
                  {deviceColumns.map((column) => (
                    <TableCell key={column.key} className={classes.nestedBodyCell}>
                      {column.key === "tradeInDate"
                        ? formatDate(device[column.key])
                        : device[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  const isTableLoading = activeTab === "pending" ? pendingLoading : shippedLoading;

  return (
    <div className={`${classes.container} ${className || ""}`}>
      <div className={classes.tabsContainer}>
        <NavigationTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabClick}
        />
      </div>

      <div className={classes.header}>
        <Typography className={classes.title}>
          {activeTab === "pending" ? t("Pending Devices") : t("Shipped Devices")}
        </Typography>
        {activeTab === "pending" && (
          <Button
            className={classes.createButton}
            onClick={handleCreateShipmentClick}
            variant="contained"
            disabled={selectedDevices.length === 0}
          >
            <CreateShipmentIcon className={classes.buttonIcon} />
            {t("Create Shipment")}
          </Button>
        )}
      </div>

      <div className={classes.searchBarContainer}>
        <div className={classes.combinedSearchBar}>
          <div className={classes.searchSection}>
            <svg
              className={classes.searchIcon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                fill="currentColor"
              />
            </svg>
            <input
              type="text"
              placeholder={t("Search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={classes.searchInput}
            />
          </div>
          <div className={classes.separator} />
          <div
            className={classes.filterSection}
            onClick={handleFilterClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleFilterClick(e);
              }
            }}
            aria-label="Filter dropdown"
          >
            <span className={classes.filterText}>
              {(activeTab === "pending"
                ? pendingSearchOptions
                : shippedSearchOptions
              ).find((option) => option.key === filterKey)?.label ||
                t(activeTab === "pending" ? "Trade-In ID" : "Shipment No.")}
            </span>
            <ArrowDownIcon className={classes.chevronIcon} />
          </div>
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          style: {
            marginTop: -4,
            minWidth: 150,
          },
          className: classes.filterMenuPaper,
        }}
        MenuListProps={{ disablePadding: true }}
      >
        {(activeTab === "pending"
          ? pendingSearchOptions
          : shippedSearchOptions
        ).map((option) => (
        <MenuItem
            key={option.key}
            onClick={() => handleFilterSelect(option.key)}
            className={classes.filterMenuItem}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {activeTab === "pending" && (
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontStyle: "normal",
            fontSize: "14px",
            lineHeight: "20px",
            letterSpacing: "0%",
            verticalAlign: "middle",
            color: "#495057",
            marginBottom: "16px",
          }}
        >
          {t("Items selected", { count: selectedDevices.length })}
        </div>
      )}

      <DataTable
        columns={activeTab === "pending" ? pendingColumns : shippedColumns}
        data={
          activeTab === "pending" ? filteredPendingData : filteredShippedData
        }
        showCheckbox={activeTab === "pending"}
        pagination={true}
        totalItems={
          activeTab === "pending"
            ? pendingTotal
            : shippedTotal
        }
        pageSize={
          activeTab === "pending"
            ? pendingPageSize
            : shippedPageSize
        }
        page={
          activeTab === "pending"
            ? pendingPage
            : shippedPage
        }
        onPageChange={
          activeTab === "pending"
            ? onPendingPageChange
            : onShippedPageChange
        }
        onPageSizeChange={
          activeTab === "pending"
            ? onPendingPageSizeChange
            : onShippedPageSizeChange
        }
        showExpandIcon={activeTab === "shipped"}
        renderExpandedContent={
          activeTab === "shipped" ? renderExpandedDevices : null
        }
        onRowSelect={activeTab === "pending" ? handleRowSelect : null}
        isRowSelected={activeTab === "pending" ? isRowSelected : null}
        clearSelectionSignal={
          activeTab === "pending" ? selectionResetKey : undefined
        }
        isLoading={isTableLoading}
      />

      {activeTab === "pending" && (
        <CreateShipmentModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onCreateShipment={handleCreateShipment}
          selectedDevices={selectedDevices}
          boxInventory={boxInventory}
        />
      )}
    </div>
  );
};

/** API may omit or null trade-in id before assignment. */
const tradeInIdPropType = (props, propName, componentName) => {
  const v = props[propName];
  if (
    v === undefined ||
    v === null ||
    typeof v === "string" ||
    typeof v === "number"
  ) {
    return null;
  }
  return new Error(
    `Invalid prop \`${propName}\` supplied to \`${componentName}\`.`
  );
};

PendingDevices.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      tradeInId: tradeInIdPropType,
      modelId: PropTypes.string.isRequired,
      model: PropTypes.string.isRequired,
      tradeInDate: PropTypes.string.isRequired,
    })
  ).isRequired,
  shippedData: PropTypes.arrayOf(
    PropTypes.shape({
      shipmentNo: PropTypes.string.isRequired,
      trackingNo: PropTypes.string.isRequired,
      created: PropTypes.string.isRequired,
      numDevices: PropTypes.number.isRequired,
      boxType: PropTypes.string.isRequired,
      devices: PropTypes.arrayOf(
        PropTypes.shape({
          tradeInId: tradeInIdPropType,
          modelId: PropTypes.string.isRequired,
          model: PropTypes.string.isRequired,
          tradeInDate: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  pendingLoading: PropTypes.bool,
  shippedLoading: PropTypes.bool,
  onCreateShipment: PropTypes.func,
  onTabChange: PropTypes.func,
  className: PropTypes.string,
};

PendingDevices.defaultProps = {
  shippedData: [],
  pendingLoading: false,
  shippedLoading: false,
  onCreateShipment: null,
  onTabChange: null,
  className: "",
};

export default PendingDevices;

